import React, { useEffect, useState } from 'react';
import { Building2, Clock, Link2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { usePilotContext } from '../hooks/usePilotContext';
import { cn } from '../lib/utils';

export default function CriticalInfraPage() {
  const { context, loading: ctxLoading } = usePilotContext();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const j = await api.get('/api/ot/posture-context');
        if (!cancelled) {
          setData(j);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Failed to load OT posture context');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const otMode = context.ot_power_utility_mode ?? data?.ot_power_utility_mode;
  const profile = context.deployment_profile ?? data?.deployment_profile ?? 'enterprise';

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Critical infrastructure
          </p>
          <h1 className="font-heading text-2xl font-bold tracking-tight md:text-3xl">
            OT / power utility posture
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            What AERTA covers in a utility or ICS-oriented deployment — and what requires separate engineering
            or operations programs. This page mirrors the API used for dashboards and pilot scoping.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!ctxLoading && (
            <>
              <Badge variant="outline" className="font-mono text-xs">
                {profile}
              </Badge>
              {otMode ? (
                <Badge className="bg-emerald-600/90 text-white hover:bg-emerald-600">OT mode</Badge>
              ) : (
                <Badge variant="secondary">Standard sector</Badge>
              )}
            </>
          )}
        </div>
      </div>

      {err && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          {err}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-border/80">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">In scope (cyber validation)</CardTitle>
            </div>
            <CardDescription>Capabilities this product is designed to support on authorized networks.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {(data?.what_aerta_covers ?? []).map((line) => (
                <li key={line} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary/80" />
                  <span>{line}</span>
                </li>
              ))}
              {!data && !err && (
                <li className="text-muted-foreground/70">Loading…</li>
              )}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-border/80">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500/90" />
              <CardTitle className="text-lg">Out of scope (without separate engagement)</CardTitle>
            </div>
            <CardDescription>Do not position the product as replacing these layers.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {(data?.out_of_scope_without_separate_engagement ?? []).map((line) => (
                <li key={line} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500/70" />
                  <span>{line}</span>
                </li>
              ))}
              {!data && !err && (
                <li className="text-muted-foreground/70">Loading…</li>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/80">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-sky-400/90" />
            <CardTitle className="text-lg">What &quot;24/7&quot; means here</CardTitle>
          </div>
          <CardDescription>Align customer expectations with platform vs SOC vs scheduled work.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {data?.continuous_24x7_meaning &&
              Object.entries(data.continuous_24x7_meaning).map(([key, text]) => (
                <div
                  key={key}
                  className={cn(
                    'rounded-lg border border-border bg-secondary/30 p-4',
                  )}
                >
                  <p className="mb-2 font-mono text-xs uppercase tracking-wide text-primary">{key.replace(/_/g, ' ')}</p>
                  <p className="text-sm leading-relaxed text-muted-foreground">{text}</p>
                </div>
              ))}
            {!data && !err && <p className="text-sm text-muted-foreground">Loading…</p>}
          </div>
        </CardContent>
      </Card>

      <Card className="border-dashed border-border/80 bg-secondary/20">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Lab tooling & external prototypes</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>{data?.prototype_note ?? '—'}</p>
          <div>
            <p className="mb-2 font-medium text-foreground">Deployment &amp; zoning (summary)</p>
            <ul className="space-y-2">
              {(data?.deployment_zoning_guidance ?? []).map((line) => (
                <li key={line} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary/70" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
          {data?.public_documentation_url ? (
            <p className="flex items-start gap-2 border-t border-border/60 pt-3">
              <Link2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <span>
                Extended pilot documentation:{' '}
                <a
                  href={data.public_documentation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  open documentation
                </a>
                .
              </span>
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
